import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';

import { QUERY_POST } from '../utils/queries';
import { UPDATE_POST } from '../utils/mutations';

import spinner from '../assets/spinner.gif';

import Form from 'react-bootstrap/Form';
import Toast from 'react-bootstrap/Toast';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';

import './BlogPost.css';
import ImageUpload from '../components/ImageUpload';

function BlogEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [post, setPost] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [disableSaveButton, setDisableSaveButton] = useState(false);

  const [edited, setEdited] = useState(false);
  const [titleText, setTitleText] = useState('');
  const [postText, setPostText] = useState('');
  const [activeFlag, setActiveFlag] = useState(true);
  const [imageId, setImageId] = useState(null);
  const [productLink, setProductLink] = useState(null);

  const [ getPost, { loading, error, data }] = useLazyQuery(QUERY_POST);
  const [updatePost, { data: updatePostData, loading: updatePostLoading, error: updatePostError }] =
    useMutation(UPDATE_POST);

  useEffect(() => {
    // If an ID was provided (edit page) then get post from database
    if (id) {
      getPost({ variables: { id } });
    } else {
      setPost({title: '', post: '', active: true, image: null, productLink: null});
    }
  }, [getPost, id]);

  useEffect(() => {
    if (data) {
      setTitleText(data.post.title);
      setPostText(data.post.post);
      setActiveFlag(data.post.active);
      setPost(data.post);
      setProductLink(data.post.productLink);
      setImageId(data.post.image);
    }
  }, [data]);

  useEffect(() => {
    setEdited(post && (
      titleText !== post.title ||
      postText !== post.post ||
      Boolean(activeFlag) !== Boolean(post.active) ||
      imageId !== post.image ||
      productLink !== post.productLink
    ));
  }, [post, titleText, postText, activeFlag, imageId, productLink]);

  function confirmSave() {
    setDisableSaveButton(false);
    setShowConfirm(true);
  }

  function savePost() {
    setDisableSaveButton(true);
    updatePost(
      {
        variables: {
          _id: post._id || undefined,
          title: titleText,
          post: postText,
          image: imageId,
          active: Boolean(activeFlag),
          productLink: productLink
        }
      })
      .then(({data: { addOrUpdatePost: { _id }}}) => {
          if (! post._id) post._id = _id;
          setShowConfirm(false);
          setShowSaved(true);
          navigate(`/blog/${_id}`);
        });
  }

  function imageUploaded(id) {
    setImageId(id);
  }

  if (error) {
    return `Query error: ${error.message}`;
  }

  return (
    <>
      <nav className="breadcrumb"><Link to="/blog">Blog</Link></nav>

      {post ? (
        <div className="blog-post">

          <h2>{post.title}</h2>

          <Form>

            <Form.Group className="mb-3">
              <Form.Label>Title:</Form.Label>
              <Form.Control type="text" name="title" placeholder="Put the title here..."
                value={titleText} onChange={e => setTitleText(e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Post contents:</Form.Label>
              <Form.Control as="textarea" name="post" rows="20"
                placeholder="Write your post here..."
                value={postText} onChange={e => setPostText(e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Product affiliate link:</Form.Label>
              <Form.Control type="text" name="productLink" placeholder="Put the URL here..."
                value={productLink} onChange={e => setProductLink(e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Post should be visible on site:</Form.Label>
              <Form.Check
                  type="checkbox"
                  label="Active"
                  value={true}
                  checked={activeFlag}
                  onChange={e => setActiveFlag(e.target.checked)}
                />
            </Form.Group>

            <ImageUpload initialImageId={imageId} onSuccess={id => imageUploaded(id)} />

            <Button type="button" variant="primary" disabled={! edited}
              onClick={() => confirmSave()}>Save Changes to {process.env.NODE_ENV} site</Button>

          </Form>


          <Modal show={showConfirm} onHide={() => setShowConfirm(false)}>
            <Modal.Dialog>
              <Modal.Header closeButton>
                <Modal.Title>Confirm</Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <p>Save your changes to the database?</p>
              </Modal.Body>

              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowConfirm(false)}>Cancel</Button>
                <Button variant="primary" disabled={disableSaveButton} onClick={() => savePost()}>Save and View Post</Button>
              </Modal.Footer>
            </Modal.Dialog>
          </Modal>

        </div>
      ) : null}
      {loading ? <img src={spinner} alt="loading" /> : null}
    </>
  );
}

export default BlogEdit;

